// Apply an extension's organization entity records through the existing
// entity, location, and license routes. The name is stored as given. Kind
// stays the API default, organization, and is not inferred from the name.

export const ORGANIZATION_ENTITY_RECORDS = "organization.entities.v1";
const MAX_TEXT = 200;
const MAX_ENTITIES = 32;
const MAX_LOCATIONS = 64;

export interface OrganizationEntityLocationRecord {
  name: string;
  licenseNumber: string;
  licenseType: string;
  jurisdiction: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  countryCode?: string;
}

export interface OrganizationEntityRecord {
  name: string;
  locations: OrganizationEntityLocationRecord[];
}

export interface ExistingOrganizationDirectory {
  entities: { id: string; name: string; slug: string }[];
  locations: { id: string; code: string | null }[];
  licenses: { id: string; legalEntityId: string; locationId: string | null; licenseNumber: string }[];
}

export type OrganizationEntityWrite =
  | { kind: "entity"; name: string; slug: string }
  | {
    kind: "location";
    code: string;
    name: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    countryCode?: string;
  }
  | { kind: "occupancy"; entityName: string; locationCode: string }
  | {
    kind: "license";
    entityName: string;
    locationCode: string;
    regulator: string;
    jurisdiction: string;
    licenseNumber: string;
    licenseType: string;
  };

function trimmed(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text || text.length > MAX_TEXT || text.includes("\u0000")) return null;
  return text;
}

function optionalText(record: Record<string, unknown>, key: string): string | undefined | null {
  if (!(key in record)) return undefined;
  return trimmed(record[key]);
}

export function organizationEntitySlug(name: string): string | null {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (slug.length < 3 || slug.length > 63 || slug.includes("--")) return null;
  return slug;
}

export function readOrganizationEntityRecords(
  body: unknown,
):
  | { status: "absent" }
  | { status: "invalid" }
  | { status: "records"; entities: OrganizationEntityRecord[]; purposePrefix: string | null } {
  if (!body || typeof body !== "object" || Array.isArray(body) || !("records" in body)) {
    return { status: "absent" };
  }
  const record = body as Record<string, unknown>;
  const keys = Object.keys(record);
  const allowed = new Set(["records", "entities", "purpose_prefix"]);
  if (
    record.records !== ORGANIZATION_ENTITY_RECORDS
    || !keys.includes("entities")
    || keys.some((key) => !allowed.has(key))
    || !Array.isArray(record.entities)
    || record.entities.length === 0
    || record.entities.length > MAX_ENTITIES
  ) {
    return { status: "invalid" };
  }
  let purposePrefix: string | null = null;
  if ("purpose_prefix" in record) {
    if (typeof record.purpose_prefix !== "string" || !/^[a-z][a-z0-9_-]{0,40}:$/.test(record.purpose_prefix)) {
      return { status: "invalid" };
    }
    purposePrefix = record.purpose_prefix;
  }
  const entities: OrganizationEntityRecord[] = [];
  const seenNames = new Set<string>();
  const seenLicenses = new Set<string>();
  let locationCount = 0;
  for (const entityValue of record.entities) {
    if (!entityValue || typeof entityValue !== "object" || Array.isArray(entityValue)) {
      return { status: "invalid" };
    }
    const entity = entityValue as Record<string, unknown>;
    const entityKeys = Object.keys(entity);
    if (
      entityKeys.length !== 2
      || !entityKeys.includes("name")
      || !entityKeys.includes("locations")
      || !Array.isArray(entity.locations)
      || entity.locations.length === 0
    ) {
      return { status: "invalid" };
    }
    const name = trimmed(entity.name);
    if (!name || seenNames.has(name)) return { status: "invalid" };
    seenNames.add(name);
    const locations: OrganizationEntityLocationRecord[] = [];
    for (const locationValue of entity.locations) {
      locationCount += 1;
      if (locationCount > MAX_LOCATIONS) return { status: "invalid" };
      if (!locationValue || typeof locationValue !== "object" || Array.isArray(locationValue)) {
        return { status: "invalid" };
      }
      const location = locationValue as Record<string, unknown>;
      if (Object.keys(location).some((key) => ![
        "name",
        "license_number",
        "license_type",
        "jurisdiction",
        "address_line1",
        "address_line2",
        "city",
        "region",
        "postal_code",
        "country_code",
      ].includes(key))) {
        return { status: "invalid" };
      }
      const locationName = trimmed(location.name);
      const licenseNumber = trimmed(location.license_number);
      const licenseType = trimmed(location.license_type);
      const jurisdiction = trimmed(location.jurisdiction);
      if (
        !locationName
        || !licenseNumber
        || !licenseType
        || !jurisdiction
        || !/^[A-Z]{2}$/.test(jurisdiction)
        || seenLicenses.has(licenseNumber)
      ) {
        return { status: "invalid" };
      }
      seenLicenses.add(licenseNumber);
      const addressLine1 = optionalText(location, "address_line1");
      const addressLine2 = optionalText(location, "address_line2");
      const city = optionalText(location, "city");
      const region = optionalText(location, "region");
      const postalCode = optionalText(location, "postal_code");
      const countryCode = optionalText(location, "country_code");
      if (
        addressLine1 === null
        || addressLine2 === null
        || city === null
        || region === null
        || postalCode === null
        || countryCode === null
      ) {
        return { status: "invalid" };
      }
      locations.push({
        name: locationName,
        licenseNumber,
        licenseType,
        jurisdiction,
        ...(addressLine1 ? { addressLine1 } : {}),
        ...(addressLine2 ? { addressLine2 } : {}),
        ...(city ? { city } : {}),
        ...(region ? { region } : {}),
        ...(postalCode ? { postalCode } : {}),
        ...(countryCode ? { countryCode } : {}),
      });
    }
    entities.push({ name, locations });
  }
  return { status: "records", entities, purposePrefix };
}

export function planOrganizationEntityWrites(
  entities: OrganizationEntityRecord[],
  existing: ExistingOrganizationDirectory,
): { ok: true; writes: OrganizationEntityWrite[] } | { ok: false } {
  const byName = new Map<string, { id: string; slug: string }[]>();
  const slugs = new Set<string>();
  for (const entity of existing.entities) {
    const name = entity.name.trim();
    const matches = byName.get(name) ?? [];
    matches.push({ id: entity.id, slug: entity.slug });
    byName.set(name, matches);
    slugs.add(entity.slug);
  }
  const holderName = new Map<string, string>();
  for (const entity of existing.entities) holderName.set(entity.id, entity.name.trim());
  const licenseHolder = new Map<string, string>();
  const licensesOnLocation = new Map<string, string[]>();
  for (const license of existing.licenses) {
    const holder = holderName.get(license.legalEntityId);
    if (!holder) return { ok: false };
    licenseHolder.set(license.licenseNumber.trim(), holder);
    if (license.locationId) {
      const holders = licensesOnLocation.get(license.locationId) ?? [];
      holders.push(holder);
      licensesOnLocation.set(license.locationId, holders);
    }
  }
  const locationByCode = new Map<string, string[]>();
  for (const location of existing.locations) {
    if (!location.code) continue;
    const ids = locationByCode.get(location.code) ?? [];
    ids.push(location.id);
    locationByCode.set(location.code, ids);
  }

  const writes: OrganizationEntityWrite[] = [];
  const creating = new Set<string>();
  for (const entity of entities) {
    const matches = byName.get(entity.name) ?? [];
    if (matches.length > 1) return { ok: false };
    if (matches.length === 0 && !creating.has(entity.name)) {
      const slug = organizationEntitySlug(entity.name);
      if (!slug || slugs.has(slug)) return { ok: false };
      slugs.add(slug);
      creating.add(entity.name);
      writes.push({ kind: "entity", name: entity.name, slug });
    }
    for (const location of entity.locations) {
      const holder = licenseHolder.get(location.licenseNumber);
      if (holder) {
        if (holder !== entity.name) return { ok: false };
        continue;
      }
      const codes = locationByCode.get(location.licenseNumber) ?? [];
      if (codes.length > 1) return { ok: false };
      if (codes.length === 1) {
        const holders = licensesOnLocation.get(codes[0]!) ?? [];
        if (holders.some((name) => name !== entity.name)) return { ok: false };
      } else {
        writes.push({
          kind: "location",
          code: location.licenseNumber,
          name: location.name,
          ...(location.addressLine1 ? { addressLine1: location.addressLine1 } : {}),
          ...(location.addressLine2 ? { addressLine2: location.addressLine2 } : {}),
          ...(location.city ? { city: location.city } : {}),
          ...(location.region ? { region: location.region } : {}),
          ...(location.postalCode ? { postalCode: location.postalCode } : {}),
          ...(location.countryCode ? { countryCode: location.countryCode } : {}),
        });
      }
      writes.push(
        { kind: "occupancy", entityName: entity.name, locationCode: location.licenseNumber },
        {
          kind: "license",
          entityName: entity.name,
          locationCode: location.licenseNumber,
          regulator: location.jurisdiction,
          jurisdiction: location.jurisdiction,
          licenseNumber: location.licenseNumber,
          licenseType: location.licenseType,
        },
      );
    }
  }
  return { ok: true, writes };
}

export interface OrganizationEntityWriter {
  createEntity(input: { name: string; slug: string }): Promise<{ id: string }>;
  createLocation(input: {
    code: string;
    name: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    countryCode?: string;
  }): Promise<{ id: string }>;
  listRelationships(locationId: string): Promise<{ legalEntityId: string; kind: string }[]>;
  createOccupancy(input: { locationId: string; legalEntityId: string }): Promise<void>;
  createLicense(input: {
    locationId: string;
    legalEntityId: string;
    regulator: string;
    jurisdiction: string;
    licenseNumber: string;
    licenseType: string;
  }): Promise<{ id: string }>;
}

export async function applyOrganizationEntityWrites(
  entities: OrganizationEntityRecord[],
  existing: ExistingOrganizationDirectory,
  writer: OrganizationEntityWriter,
): Promise<{ ok: true; licenseIds: string[] } | { ok: false }> {
  const plan = planOrganizationEntityWrites(entities, existing);
  if (!plan.ok) return { ok: false };
  const entityIds = new Map(existing.entities.map((entity) => [entity.name.trim(), entity.id]));
  const locationIds = new Map<string, string>();
  for (const location of existing.locations) {
    if (location.code && !locationIds.has(location.code)) locationIds.set(location.code, location.id);
  }
  const licenseIds = new Map(existing.licenses.map((license) => [license.licenseNumber.trim(), license.id]));
  try {
    for (const write of plan.writes) {
      if (write.kind === "entity") {
        const created = await writer.createEntity({ name: write.name, slug: write.slug });
        entityIds.set(write.name, created.id);
      } else if (write.kind === "location") {
        const created = await writer.createLocation(write);
        locationIds.set(write.code, created.id);
      } else if (write.kind === "occupancy") {
        const legalEntityId = entityIds.get(write.entityName);
        const locationId = locationIds.get(write.locationCode);
        if (!legalEntityId || !locationId) return { ok: false };
        const relationships = await writer.listRelationships(locationId);
        if (relationships.some((item) => item.legalEntityId === legalEntityId && item.kind === "occupancy")) {
          continue;
        }
        await writer.createOccupancy({ locationId, legalEntityId });
      } else {
        const legalEntityId = entityIds.get(write.entityName);
        const locationId = locationIds.get(write.locationCode);
        if (!legalEntityId || !locationId) return { ok: false };
        const created = await writer.createLicense({
          locationId,
          legalEntityId,
          regulator: write.regulator,
          jurisdiction: write.jurisdiction,
          licenseNumber: write.licenseNumber,
          licenseType: write.licenseType,
        });
        licenseIds.set(write.licenseNumber, created.id);
      }
    }
  } catch {
    return { ok: false };
  }
  const ids: string[] = [];
  for (const entity of entities) {
    for (const location of entity.locations) {
      const id = licenseIds.get(location.licenseNumber);
      if (!id) return { ok: false };
      ids.push(id);
    }
  }
  return { ok: true, licenseIds: ids };
}
